/*
  Malpetit · Product card quick-add
  Event-delegated, loaded once globally (layout/theme.liquid).

   - [data-mp-add-toggle]  → reveal / hide the size row (multi-variant)
   - [data-mp-add-variant] → add that variant to the cart
                             (single-variant button OR a size button inside the size row)

  After a successful add, the primary button switches into "View bag →"
  state — clicking it navigates to /cart instead of adding again. This
  mirrors the PDP behaviour (Added → View bag link). State auto-reverts
  after ADDED_HOLD_MS so the customer can pick a different size on the
  same card without waiting.
*/
(function () {
  'use strict';

  var ADDED_HOLD_MS = 3500;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root)
      ? Shopify.routes.root
      : '/';
  }

  function cartUrl() {
    return root() + 'cart';
  }

  document.addEventListener('click', function (e) {
    // 1) Primary button in "added" state — any click on it navigates to cart,
    //    regardless of whether it's a toggle button or a single-variant button.
    var primary = e.target.closest('.mp-card__add-btn');
    if (primary && primary.dataset.mpAddedState === '1') {
      e.preventDefault();
      goToCart(primary);
      return;
    }

    // 2) Multi-variant case: toggle size row + swap label Add↔Cancel
    var toggle = e.target.closest('[data-mp-add-toggle]');
    if (toggle) {
      e.preventDefault();
      var wrapT = toggle.closest('[data-mp-add-wrap]');
      var sizes = wrapT && wrapT.querySelector('[data-mp-sizes]');
      var label = toggle.querySelector('.mp-card__add-label');
      if (sizes) {
        sizes.hidden = !sizes.hidden;
        toggle.setAttribute('aria-expanded', sizes.hidden ? 'false' : 'true');
        if (wrapT) wrapT.classList.toggle('is-picking-size', !sizes.hidden);
        if (label) {
          if (!label.dataset.orig) label.dataset.orig = label.textContent;
          label.textContent = sizes.hidden
            ? label.dataset.orig
            : window.mpStrings.card.cancel;
        }
      }
      return;
    }

    // 3) Direct add: single-variant button OR size button inside the size row
    var addBtn = e.target.closest('[data-mp-add-variant]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.hasAttribute('disabled')) return;
      var variantId = addBtn.getAttribute('data-mp-add-variant');
      if (variantId) addToCart(addBtn, variantId);
    }
  });

  function addToCart(btn, variantId) {
    var wrap = btn.closest('[data-mp-add-wrap]');
    if (!wrap || wrap.dataset.busy === '1') return;
    wrap.dataset.busy = '1';

    // OPTIMISTIC: switch primary button to "View bag →" immediately,
    // bump cart count + animate icon. Don't wait for slow /cart/add.js.
    showAdded(wrap);
    bumpCount(1);
    bumpCartIcon();

    // Store the in-flight promise on the wrap so a fast View-bag tap can
    // await it before navigating — prevents landing on an empty cart page.
    var promise = fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) { throw new Error('add failed'); }
        return r.json();
      })
      .catch(function (err) {
        // Revert optimistic count + show error briefly
        bumpCount(-1);
        showError(wrap, window.mpStrings.card.try_again);
        throw err;
      })
      .then(function (data) {
        wrap.dataset.busy = '0';
        wrap.dataset.added = '1';
        wrap._mpAddPromise = null;
        return data;
      });

    wrap._mpAddPromise = promise;
  }

  // Click on "View bag →" — only navigate AFTER the add has actually
  // committed on the server. Show a spinner state while we wait.
  function goToCart(btn) {
    var wrap = btn.closest('[data-mp-add-wrap]');
    var pending = wrap && wrap._mpAddPromise;
    if (!pending) {
      window.location.href = cartUrl();
      return;
    }
    // Show spinner state, block double-clicks
    btn.classList.add('is-pending');
    btn.setAttribute('aria-busy', 'true');
    btn.style.pointerEvents = 'none';
    pending
      .then(function () { window.location.href = cartUrl(); })
      .catch(function () {
        // Add failed — drop spinner, revert button so user can retry
        btn.classList.remove('is-pending');
        btn.removeAttribute('aria-busy');
        btn.style.pointerEvents = '';
      });
  }

  function showAdded(wrap) {
    var btn = wrap.querySelector('.mp-card__add-btn');
    var label = wrap.querySelector('.mp-card__add-label');
    if (!btn || !label) return;

    // Hide size row if open
    var sizes = wrap.querySelector('[data-mp-sizes]');
    if (sizes) {
      sizes.hidden = true;
      var toggle = wrap.querySelector('[data-mp-add-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    // Remember original label so we can revert
    if (!label.dataset.orig) label.dataset.orig = label.textContent;

    // Switch to "View bag" state
    label.textContent = window.mpStrings.card.view_bag + ' →';
    btn.classList.add('is-added');
    btn.dataset.mpAddedState = '1';
    btn.setAttribute('aria-label', window.mpStrings.card.view_bag);

    // Auto-revert after hold window — lets user add another size/variant
    if (btn._mpRevertTimer) clearTimeout(btn._mpRevertTimer);
    btn._mpRevertTimer = setTimeout(function () { revertButton(wrap); }, ADDED_HOLD_MS);
  }

  function revertButton(wrap) {
    var btn = wrap.querySelector('.mp-card__add-btn');
    var label = wrap.querySelector('.mp-card__add-label');
    if (!btn || !label) return;
    if (label.dataset.orig) label.textContent = label.dataset.orig;
    btn.classList.remove('is-added');
    delete btn.dataset.mpAddedState;
    btn.removeAttribute('aria-label');
    if (btn._mpRevertTimer) { clearTimeout(btn._mpRevertTimer); btn._mpRevertTimer = null; }
  }

  function showError(wrap, msg) {
    var btn = wrap.querySelector('.mp-card__add-btn');
    var label = wrap.querySelector('.mp-card__add-label');
    if (!btn || !label) return;
    // Drop "added" state on error
    delete btn.dataset.mpAddedState;
    btn.classList.remove('is-added');
    if (btn._mpRevertTimer) { clearTimeout(btn._mpRevertTimer); btn._mpRevertTimer = null; }
    if (!label.dataset.orig) label.dataset.orig = label.textContent;
    label.textContent = msg;
    setTimeout(function () {
      if (label.dataset.orig) label.textContent = label.dataset.orig;
    }, 1900);
  }

  function bumpCount(delta) {
    document.querySelectorAll('.mp-bag__count').forEach(function (el) {
      var cur = parseInt(el.textContent, 10) || 0;
      var next = cur + delta;
      if (next < 0) next = 0;
      el.textContent = next;
      el.hidden = (next === 0);
    });
  }

  function bumpCartIcon() {
    document.dispatchEvent(new CustomEvent('mp:cart:updated'));
  }
})();

/*
  FIT-4b · ✿ "fits your dog" badge on catalog cards.
  Reads the measurements the home Fit tool saved (cookie mp_fit) and, for every
  card that carries data-fit-chart (apparel with a size chart), resolves the
  size with the SAME matcher the PDP uses (window.mpFitMatch, inlined in
  theme.liquid). The badge shows only when that size is an IN-STOCK variant:
  the quick-add size buttons already carry `disabled` for sold-out sizes.
  Client-side on purpose: collection pages are full-page cached and Liquid
  cannot read cookies. Badge elements exist in the markup (hidden), so
  revealing them never shifts the layout.
*/
(function () {
  'use strict';
  var M = window.mpFitMatch;
  if (!M) return;
  var dog = M.dog();
  if (!dog) return;

  var FLOWER = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><g fill="currentColor"><circle cx="12" cy="5.8" r="4.3"/><circle cx="17.9" cy="10.1" r="4.3"/><circle cx="15.6" cy="17" r="4.3"/><circle cx="8.4" cy="17" r="4.3"/><circle cx="6.1" cy="10.1" r="4.3"/><circle cx="12" cy="12" r="4.6"/></g><circle cx="12" cy="12" r="2.9" fill="var(--mp-accent)"/></svg>';
  var cards = document.querySelectorAll('[data-mp-card][data-fit-chart]');
  for (var i = 0; i < cards.length; i++) {
    var card = cards[i];
    var R = M.resolve(M.parseChart(card.getAttribute('data-fit-chart')), dog);
    if (!R || !R.confident) continue;
    var sizes = card.querySelectorAll('.mp-card__size'), btn = null;
    for (var k = 0; k < sizes.length; k++) {
      if ((sizes[k].textContent || '').trim().toUpperCase() === R.size) { btn = sizes[k]; break; }
    }
    if (!btn || btn.disabled) continue; // that size is not sold or sold out → no promise
    var badge = card.querySelector('[data-fit-badge]');
    if (badge) badge.hidden = false;
    card.classList.add('is-fit');
    var star = document.createElement('span');
    star.className = 'mp-card__size-star';
    star.setAttribute('aria-hidden', 'true');
    star.innerHTML = FLOWER; // same SVG as the toprow badge in mp-product-card.liquid
    btn.appendChild(star);
  }
})();
